import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, inputCls } from "@/components/LegsEditor";
import { CATEGORIES, legRoute } from "@/lib/format";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, errMsg } from "@/lib/api";
import AircraftInput from "@/components/AircraftInput";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { money } from "@/lib/format";

const BLANK = {
  operator_name: "", operator_certificate: "", aircraft_type: "", aircraft_category: "midsize", tail_number: "",
  year: "", year_refurbished: "", seats: "", price: "", customer_price: "", currency: "USD", flight_time: "", amenities: "", notes: "", internal_notes: "", legs_covered: [],
};
const TEXT = [
  ["operator_name", "Operator", "Eagle Air", true],
  ["aircraft_type", "Aircraft type", "Citation XLS+", true],
  ["tail_number", "Tail number (optional)", "N123JS", false],
  ["year", "Year of Make (YOM)", "2018", false, "number"],
  ["year_refurbished", "Year of Refurbishment (YOR)", "2023", false, "number"],
  ["seats", "Seats", "8", false, "number"],
  ["price", "Cost from operator", "28500", true, "number"],
  ["customer_price", "Price to customer", "32000", false, "number"],
  ["flight_time", "Est. flight time", "2h 45m", false],
];

function useFlightEstimate(api, open, tripId, aircraftType, category, setForm, legs) {
  const last = useRef(null);
  const [busy, setBusy] = useState(false);
  const legKey = (legs || []).join(",");
  const fetchEstimate = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/trips/${tripId}/flight-time-estimate`, { params: { category, aircraft_type: aircraftType, legs: legKey } });
      if (!data.text) { if (force) toast.error("Couldn't estimate this route. Please type the flight time."); return; }
      setForm((f) => (force || !f.flight_time || f.flight_time === last.current ? { ...f, flight_time: data.text } : f));
      last.current = data.text;
    } catch { /* manual entry still works */ } finally { setBusy(false); }
  };
  useEffect(() => {
    if (!open) { last.current = null; return undefined; }
    const t = setTimeout(() => fetchEstimate(false), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tripId, aircraftType, category, legKey]);
  return [busy, () => fetchEstimate(true)];
}

function LegsPicker({ legs, value, onChange }) {
  const all = legs.map((_, i) => i);
  const on = value.length ? value : all;
  const toggle = (i) => {
    const next = on.includes(i) ? on.filter((x) => x !== i) : [...on, i].sort((a, b) => a - b);
    if (!next.length) return;
    onChange(next.length === legs.length ? [] : next);
  };
  return (
    <Field label="Legs this aircraft covers">
      <div className="grid gap-2 sm:grid-cols-2" data-testid="option-legs-picker">
        {legs.map((l, i) => (
          <label key={i} className="flex items-center gap-2 text-sm text-zinc-300 border border-white/10 px-3 py-2 cursor-pointer">
            <Checkbox checked={on.includes(i)} onCheckedChange={() => toggle(i)} data-testid={`option-leg-${i}-checkbox`} />
            Leg {i + 1} · {legRoute(l)}
          </label>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500 mt-1" data-testid="option-legs-hint">{value.length ? "This option covers only the ticked legs." : "Covers all legs. Untick a leg if this aircraft is only quoted for some of them."}</p>
    </Field>
  );
}

function FlightTimeField({ value, onChange, busy, onRecalc }) {
  return (
    <>
      <div className="flex gap-2">
        <Input className={inputCls} value={value} onChange={onChange} placeholder="2h 45m" data-testid="option-flight_time-input" />
        <button type="button" className="frame-btn !px-3 shrink-0" onClick={onRecalc} disabled={busy} title="Recalculate from route and aircraft" data-testid="option-flight-time-recalc-btn">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
        </button>
      </div>
      <p className="text-[11px] text-zinc-500 mt-1" data-testid="option-flight-time-hint">Estimated from the route and aircraft. Type over it if the operator gives an exact time.</p>
    </>
  );
}

function toPayload(f, notify) {
  const num = (v) => (v === "" || v === null ? null : Number(v));
  return { ...f, year: num(f.year), year_refurbished: num(f.year_refurbished), seats: num(f.seats), price: Number(f.price), customer_price: num(f.customer_price), notify };
}

const keyOf = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

function useListedCategory(api, open, name, setForm) {
  const [locked, setLocked] = useState(null);
  const apply = (a) => {
    const cat = a?.category || null;
    setLocked(cat);
    if (cat) { setForm((f) => ({ ...f, aircraft_category: cat })); }
  };
  useEffect(() => {
    const q = (name || "").trim();
    if (!open || q.length < 2) { setLocked(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/aircraft", { params: { q } });
        const hit = data.find((a) => keyOf(a.name) === keyOf(q));
        setLocked(hit?.category || null);
        if (hit?.category) { setForm((f) => ({ ...f, aircraft_category: hit.category })); }
      } catch { setLocked(null); }
    }, 200);
    return () => clearTimeout(t);
  }, [api, open, name, setForm]);
  return [locked, apply];
}

function OverBudgetConfirm({ info, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!info} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent className="bg-[#111319] border-white/10" data-testid="over-budget-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif-display text-2xl font-normal">This option is over the Jet Card budget</AlertDialogTitle>
          <AlertDialogDescription data-testid="over-budget-message">{info?.message} Do you still want to add it? It will be highlighted as over budget and the broker will be emailed.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="over-budget-cancel-btn">Go back</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="over-budget-confirm-btn">Add anyway</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function useOperators(api, open) {
  const [ops, setOps] = useState([]);
  useEffect(() => {
    if (open) { api.get("/operators").then((r) => setOps(r.data)).catch(() => setOps([])); }
  }, [api, open]);
  return ops;
}

export default function OptionDialog({ open, onOpenChange, tripId, trip, option, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState(BLANK);
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overInfo, setOverInfo] = useState(null);
  const jetCard = trip?.client_type === "jet_card";
  const budget = jetCard ? trip?.jet_card_budget : null;
  const fields = jetCard ? TEXT.filter(([k]) => k !== "customer_price") : TEXT;
  const [locked, applyListed] = useListedCategory(api, open, form.aircraft_type, setForm);
  const [estBusy, recalc] = useFlightEstimate(api, open, tripId, form.aircraft_type, form.aircraft_category, setForm, form.legs_covered);

  useEffect(() => {
    if (open) {
      setForm(option ? Object.fromEntries(Object.keys(BLANK).map((k) => [k, k === "legs_covered" ? option.legs_covered || [] : option[k] ?? ""])) : BLANK);
      setNotify(true);
    }
  }, [open, option]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const ops = useOperators(api, open);
  const op = ops.find((o) => keyOf(o.name) === keyOf(form.operator_name));
  const pickOperator = (e) => {
    const v = e.target.value;
    const hit = ops.find((o) => keyOf(o.name) === keyOf(v));
    setForm((f) => ({ ...f, operator_name: v, ...(hit?.certificate && !f.operator_certificate ? { operator_certificate: hit.certificate } : {}) }));
  };
  const pickTail = (e) => {
    const v = e.target.value.toUpperCase();
    const t = op?.tails.find((x) => x.tail_number === v);
    setForm((f) => ({ ...f, tail_number: v, ...(t ? { aircraft_type: t.aircraft_type || f.aircraft_type, aircraft_category: t.aircraft_category || f.aircraft_category,
      year: t.year ?? f.year, year_refurbished: t.year_refurbished ?? f.year_refurbished, seats: t.seats ?? f.seats } : {}) }));
  };

  const save = async (e, confirm = false) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...toPayload(form, notify), confirm_over_budget: confirm };
      if (jetCard) delete payload.customer_price;
      const { data } = option
        ? await api.patch(`/options/${option.id}`, payload)
        : await api.post(`/trips/${tripId}/options`, payload);
      toast.success(data.over_budget ? "Option saved and flagged as over budget. Broker emailed." : option ? "Option updated" : "Option added and ranked by price");
      setOverInfo(null);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "over_budget") setOverInfo(d);
      else toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="option-dialog">
        <DialogHeader>
          <p className="eyebrow">{option ? "Edit option" : "New operator option"}</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">{option ? option.aircraft_type : "Aircraft option details"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map(([k, label, ph, req, type]) => (
              <div key={k} className={k === "operator_name" || k === "flight_time" || k === "seats" ? "sm:col-span-2" : ""}>
              <Field label={label}>
                {k === "aircraft_type" ? (
                  <AircraftInput className={inputCls} value={form[k]} onChange={set(k)} onSelect={applyListed} category={form.aircraft_category} placeholder="Start typing, e.g. Challenger" required />
                ) : k === "flight_time" ? (
                  <FlightTimeField value={form.flight_time} onChange={set("flight_time")} busy={estBusy} onRecalc={recalc} />
                ) : k === "operator_name" || k === "tail_number" ? (
                  <>
                    <Input className={inputCls} value={form[k]} onChange={k === "operator_name" ? pickOperator : pickTail} placeholder={ph} required={req}
                      list={`option-${k}-list`} autoComplete="off" data-testid={`option-${k}-input`} />
                    <datalist id={`option-${k}-list`}>
                      {k === "operator_name" ? ops.map((o) => <option key={o.id} value={o.name} />) : (op?.tails || []).map((t) => <option key={t.tail_number} value={t.tail_number}>{t.aircraft_type}</option>)}
                    </datalist>
                  </>
                ) : (
                  <Input className={inputCls} type={type || "text"} step={k === "price" || k === "customer_price" ? "0.01" : undefined} value={form[k]} onChange={set(k)} placeholder={ph} required={req} data-testid={`option-${k}-input`} />
                )}
                {k === "price" && jetCard && (
                  <p className={`text-[11px] mt-1 ${budget && Number(form.price) > budget ? "text-rose-400" : "text-zinc-500"}`} data-testid="option-budget-hint">
                    {budget
                      ? Number(form.price) > budget
                        ? `${money(Number(form.price) - budget, form.currency)} over the Jet Card budget of ${money(budget, form.currency)}`
                        : `Jet Card budget ${money(budget, form.currency)}. This is the price to customer.`
                      : "No Jet Card budget set on this trip yet."}
                  </p>
                )}
              </Field>
              </div>
            ))}
            <Field label="Aircraft category">
              <Select value={form.aircraft_category} onValueChange={set("aircraft_category")} disabled={!!locked}>
                <SelectTrigger className={inputCls} data-testid="option-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              {locked && <p className="text-[11px] text-zinc-500 mt-1" data-testid="option-category-locked-hint">Set by the aircraft list. Admins can change it on the Aircraft page.</p>}
            </Field>
            <Field label="Currency">
              <Select value={jetCard ? "USD" : form.currency} onValueChange={set("currency")} disabled={jetCard}>
                <SelectTrigger className={inputCls} data-testid="option-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["USD", "EUR", "GBP", "CAD"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          {trip?.legs?.length > 1 && <LegsPicker legs={trip.legs} value={form.legs_covered} onChange={(v) => setForm((f) => ({ ...f, legs_covered: v }))} />}
          <Field label="Amenities">
            <Input className={inputCls} value={form.amenities} onChange={set("amenities")} placeholder="Wi-Fi, enclosed lav, catering" data-testid="option-amenities-input" />
          </Field>
          <Field label="Notes (visible to broker)">
            <Textarea className="bg-[#0d0f14] border-white/10" value={form.notes} onChange={set("notes")} data-testid="option-notes-input" />
          </Field>
          <Field label="Internal notes (flight team only)">
            <Textarea className="bg-[#0d0f14] border-white/10" value={form.internal_notes} onChange={set("internal_notes")} data-testid="option-internal-notes-input" />
          </Field>
          <label className="flex items-center gap-3 text-sm text-zinc-300">
            <Switch checked={notify} onCheckedChange={setNotify} data-testid="option-notify-switch" />
            Email the broker about this update
          </label>
          <p className="text-[11px] text-zinc-500">Photos: after saving, hover a photo on the option card and click Upload to add the operator's exterior/interior shots. With a tail number they're saved to that tail's library and reused on future quotes. Without uploads the option shows the representative pictures for its aircraft category.</p>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="frame-btn" onClick={() => onOpenChange(false)} data-testid="option-cancel-btn">Cancel</button>
            <button type="submit" className="frame-btn gold" disabled={saving} data-testid="option-save-btn">{saving ? "Saving…" : option ? "Save option" : "Add option"}</button>
          </div>
        </form>
        <OverBudgetConfirm info={overInfo} onCancel={() => setOverInfo(null)} onConfirm={() => save(null, true)} />
      </DialogContent>
    </Dialog>
  );
}
